import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Radio, FileText, FlaskConical, Newspaper, Coins, Sparkles } from "lucide-react";
import { Toaster } from "sonner";
import { ContractBadge } from "@/components/ContractBadge";

const links = [
  { to: "/",           label: "HQ",         icon: Radio },
  { to: "/manifesto",  label: "Manifesto",  icon: FileText },
  { to: "/tokenomics", label: "Tokenomics", icon: Coins },
  { to: "/meme",       label: "Meme Lab",   icon: Sparkles },
  { to: "/vault",      label: "UAP Vault",  icon: FlaskConical },
  { to: "/news",       label: "Disclosure", icon: Newspaper },
];

const TickerBanner = () => {
  const items = [
    "$PAUL on Solana",
    "the alien who finally logged on",
    "60 years at Area 51",
    "@alienpaul_sol",
    "disclosure is a mood",
    "make memes not war",
    "$PAUL on Solana",
    "still here",
  ];
  return (
    <div className="bg-ink text-white border-b border-white-2 overflow-hidden" data-testid="ticker-banner">
      <div className="tape-track py-2 font-mono text-[12px] tracking-[0.25em] uppercase">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="px-6 inline-flex items-center gap-3">
            <span className="text-green">◉</span> {t}
          </span>
        ))}
      </div>
    </div>
  );
};

export const Header = () => {
  return (
    <>
      <TickerBanner />
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-ink/60 border-b border-white-2"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <img
              src="/paul.jpg"
              alt="Paul"
              className="w-10 h-10 object-cover border-2 border-white rounded-full"
              style={{ objectPosition: "50% 30%" }}
            />
            <div>
              <div className="font-neon text-2xl leading-none text-white">
                <span className="font-mono font-extrabold mr-1">$</span>PAUL
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-white/70">@alienpaul_sol · SOL</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {links.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) => `nav-link flex items-center gap-2 ${isActive ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={13} />
                {label}
              </NavLink>
            ))}
          </nav>

          <a
            href="https://x.com/alienpaul_sol"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-paul green hidden sm:inline-flex"
            data-testid="follow-x-btn"
          >
            Follow $PAUL
          </a>
        </div>

        {/* Contract-address sub-bar — copy-enabled; update CA in /components/ContractBadge.jsx */}
        <div className="border-t border-white-2 bg-ink/40" data-testid="contract-bar">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3 flex-wrap">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/70">
              $PAUL Contract
            </span>
            <span className="hidden sm:inline text-white/30">·</span>
            <ContractBadge size="sm" dataTestid="contract-address" />
            <span className="hidden md:inline text-white/50 font-mono text-[10px] tracking-[0.25em] uppercase">
              drops at launch · solana · spl
            </span>
          </div>
        </div>
      </header>
      {/* Global toaster for copy feedback */}
      <Toaster position="top-right" richColors />
    </>
  );
};

export const Footer = () => (
  <footer className="mt-24 border-t border-white-2 bg-ink/70 backdrop-blur-md" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
      <div>
        <div className="font-neon text-3xl text-white">
          <span className="font-mono font-extrabold mr-1">$</span>PAUL
        </div>
        <p className="font-type text-white/80 text-sm mt-3 leading-relaxed">
          Unofficial fan HQ for Paul from the 2011 movie — and home of the $PAUL memecoin on Solana.
          This site is parody and celebration. UFO/UAP files are summaries of real public-record and
          FOIA materials with links to primary sources.
        </p>
      </div>
      <div>
        <div className="font-mono text-xs text-green tracking-[0.25em]">PRIMARY SOURCES</div>
        <ul className="mt-3 space-y-2 font-type text-sm">
          <li><a className="text-white hover:text-green" href="https://vault.fbi.gov/UFO" target="_blank" rel="noreferrer">FBI Vault — UFO</a></li>
          <li><a className="text-white hover:text-green" href="https://www.nsa.gov/Helpful-Links/NSA-FOIA/Declassification-Transparency-Initiatives/FOIA-Reports-and-Releases/" target="_blank" rel="noreferrer">NSA FOIA Releases</a></li>
          <li><a className="text-white hover:text-green" href="https://www.archives.gov/research/military/air-force/ufos" target="_blank" rel="noreferrer">National Archives — USAF</a></li>
          <li><a className="text-white hover:text-green" href="https://www.cia.gov/readingroom/" target="_blank" rel="noreferrer">CIA Reading Room</a></li>
        </ul>
      </div>
      <div>
        <div className="font-mono text-xs text-green tracking-[0.25em]">TRANSMISSION</div>
        <p className="font-crt text-white text-lg leading-snug mt-3">
          &gt; signal stable<br/>
          &gt; latitude: 37.2350° N<br/>
          &gt; longitude: 115.8111° W<br/>
          &gt; status: <span className="cursor-blink">still here</span>
        </p>
      </div>
    </div>
    <div className="border-t border-white-2 py-6 text-center font-mono text-[11px] tracking-[0.25em] text-white/70">
      © 2026 $PAUL · @ALIENPAUL_SOL · BEAM ME UP WHEN YOU'RE READY
    </div>

    {/* Disclaimers */}
    <div className="border-t border-white-2 bg-black/40" data-testid="footer-disclaimer">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="inline-block px-3 py-1.5 font-mono text-[11px] font-bold tracking-[0.25em] uppercase border-2 text-red-stamp"
            style={{ borderColor: 'var(--p-red)', background: 'rgba(255,74,58,0.08)' }}
            data-testid="nfa-badge"
          >
            ⚠ Not Financial Advice
          </span>
          <span className="font-mono text-[11px] tracking-[0.2em] text-white/60 uppercase">
            $PAUL is a memecoin. Vibes only. DYOR.
          </span>
        </div>

        <div className="font-type text-white/75 text-xs leading-relaxed max-w-4xl">
          <div className="font-mono text-[11px] tracking-[0.3em] text-green mb-2 uppercase">Disclaimer</div>
          <p>
            The character "Paul" and all related imagery are from the 2011 comedy film <em>Paul</em>, directed by
            Greg Mottola and written by Simon Pegg &amp; Nick Frost. The voice and motion capture performance
            for Paul was provided by Seth Rogen.
          </p>
          <p className="mt-3">
            This website and the $PAUL memecoin project are fan-made / community-driven and are not affiliated
            with, endorsed by, or connected to the original filmmakers, Universal Pictures, Working Title Films,
            or any rights holders. The image is used here for parody and meme purposes only. All rights to the
            original character and film belong to their respective owners.
          </p>
          <p className="mt-3 font-mono text-[11px] tracking-[0.2em] text-white/55 uppercase">
            No copyright infringement is intended.
          </p>
        </div>
      </div>
    </div>
  </footer>
);
